// EditableMaterial — a material's document in its EDITING face, pure.
//
// One wiring of `MaterialDocument`'s `on*` editing callbacks to the editor
// host's handlers, shared by the two places a material is edited: the
// library detail (edit in place, library layout 2026-10-01) and the legacy
// material tab. Each callback is an intent the editor host maps to its ONE
// existing command (updateElementText, removeElement, moveElement, …) with
// the same optimistic apply + content-hash lock; this component decides and
// mutates nothing itself. There is no separate save step: every edit is
// committed as it lands, exactly as in the material tab.
//
// A blank material (one empty prose seed, or no blocks) shows the invite to
// insert a first block instead of an empty document, unless it is a module:
// its parts (included resources, which the editor has no block for) follow
// the document, and a module with nothing but parts shows just those.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { MaterialDocument } from "../Editor/MaterialDocument/MaterialDocument.js";
import { defaultRenderers } from "../Editor/renderers.js";
import type { Element } from "../Editor/types.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { type ModulePart, ModuleParts } from "../ModuleParts/ModuleParts.js";
import type { FocusTarget } from "../Navigation/types.js";
import type { EditorHostApi } from "./types.js";

// The slice of the editor host this face reads: the loaded content + wear,
// and the editing handlers.
export type MaterialEditing = Pick<
    EditorHostApi,
    | "loop"
    | "pathById"
    | "slashTrigger"
    | "slashMenuState"
    | "autoEditElementId"
    | "onAutoEditConsumed"
    | "onReview"
    | "onEditText"
    | "onEditFeedback"
    | "onMarkChoiceCorrect"
    | "onUnmarkChoiceCorrect"
    | "onAddChoice"
    | "onRemoveChoice"
    | "onMoveChoice"
    | "onAllowSingleAnswer"
    | "onAllowMultipleAnswers"
    | "onSetHeadingLevel"
    | "onMoveElement"
    | "onRemoveElement"
    | "onInputRule"
    | "onSlashTrigger"
    | "onSlashNavigate"
    | "onSlashCommitActive"
    | "onEnterBlockBelow"
    | "onDeleteEmptyBlock"
>;

const isBlankMaterial = (elements: readonly Element[]): boolean =>
    elements.every((el) => el.type === "prose" && el.markdown.trim() === "");

export const EditableMaterial = ({
    elements,
    editor,
    onInsert,
    onJumpToSource,
    target,
    parts,
    onOpenPart,
}: {
    readonly elements: readonly Element[];
    // A module's parts in reading order (empty: not a module).
    readonly parts: readonly ModulePart[];
    readonly onOpenPart: (id: string) => void;
    readonly editor: MaterialEditing;
    // Open the block palette (the editor's one insert surface).
    readonly onInsert: () => void;
    readonly onJumpToSource: () => void;
    // Where to open the document at (a review item's element), once.
    readonly target?: FocusTarget;
}): ReactNode => {
    const { t } = useLingui();
    const partsList =
        parts.length > 0 ? (
            <ModuleParts parts={parts} onOpen={onOpenPart} />
        ) : null;
    if (isBlankMaterial(elements))
        return (
            partsList ?? (
                <div className="bilda-material-empty">
                    <EmptyState
                        title={t`Add your first question`}
                        body={t`This material is empty. Insert a block to begin, or ask the assistant to draft one.`}
                    >
                        <Button variant="primary" onClick={onInsert}>
                            {t`Insert`}
                        </Button>
                    </EmptyState>
                </div>
            )
        );
    return (
        <>
            <MaterialDocument
                ariaLabel={t`Material`}
                elements={elements}
                renderers={defaultRenderers}
                annotations={editor.loop.annotations}
                reviewOpenFor={editor.loop.reviewOpenFor}
                onReview={editor.onReview}
                onEditText={editor.onEditText}
                onEditFeedback={editor.onEditFeedback}
                onMarkChoiceCorrect={editor.onMarkChoiceCorrect}
                onUnmarkChoiceCorrect={editor.onUnmarkChoiceCorrect}
                onAddChoice={editor.onAddChoice}
                onRemoveChoice={editor.onRemoveChoice}
                onMoveChoice={editor.onMoveChoice}
                onAllowSingleAnswer={editor.onAllowSingleAnswer}
                onAllowMultipleAnswers={editor.onAllowMultipleAnswers}
                onSetHeadingLevel={editor.onSetHeadingLevel}
                onMoveElement={editor.onMoveElement}
                onRemoveElement={editor.onRemoveElement}
                onJumpToSource={onJumpToSource}
                onInputRule={editor.onInputRule}
                slashTrigger={
                    editor.slashTrigger !== null
                        ? {
                              forElementId: editor.slashTrigger.forElementId,
                              state: editor.slashTrigger.state,
                          }
                        : undefined
                }
                onSlashTrigger={editor.onSlashTrigger}
                onSlashNavigate={editor.onSlashNavigate}
                onSlashCommitActive={editor.onSlashCommitActive}
                onEnterAtEnd={editor.onEnterBlockBelow}
                onBackspaceEmpty={editor.onDeleteEmptyBlock}
                slashMenu={editor.slashMenuState}
                autoEditElementId={editor.autoEditElementId ?? undefined}
                onAutoEditConsumed={editor.onAutoEditConsumed}
                revealTarget={target}
                pathById={editor.pathById}
            />
            {partsList}
        </>
    );
};
